import { FC, useEffect, useRef } from 'react';
import {
  LIGHTNING_IFRAME_READY,
  LIGHTNING_ASK_IFRAME_READY,
  LIGHTNING_CONTAINER_SIZE_CHANGE,
  RENDER_LIGHTNING_COMPONENTS,
  RenderMessage,
  LIGHTNING_RENDER_COMPLETED,
} from '@groove/lightning.out/types';

import lightningOut from 'Modules/LightningOut/lightningOut';

/**
 * Renders lightning.out components for the MV3 Extension
 *
 * @remarks
 * The MV3 Extension does not allow for any remote scripts to be loaded and
 * lightning.out is essentially just a bunch of remote scripts provided by salesforce.
 * The solution is to host this content in the web-app and render it within an
 * iframe on the omnibar.
 *
 * The solution requires messages to be send/received whenever the omnibar requests
 * lightning components to be rendered.
 */
const ModuleRoot: FC = () => {
  const container = useRef(document.createElement('div'));

  useEffect(() => {
    const messageHandler = async (ev: MessageEvent) => {
      if (ev.data.type === LIGHTNING_ASK_IFRAME_READY) {
        window.parent.postMessage({ type: LIGHTNING_IFRAME_READY }, ev.origin);
        return;
      }
      if (ev.data.type === RENDER_LIGHTNING_COMPONENTS) {
        container.current.innerHTML = '';
        const message: RenderMessage = ev.data;
        const { payload } = message;
        lightningOut.configure(payload.accessToken, payload.lightningUrl, {});

        // The ResizeObserver will send a message back to the parent whenver the
        // container changes size. The parent will then adjust the height of the
        // iframe to match the height of the content
        const observer = new ResizeObserver(entries => {
          const { target } = entries[0];
          if (target.clientHeight > 0) {
            if (target.scrollWidth > target.clientWidth) {
              // IMPORTANT - this is required to fix jumpy behavior similar to
              // https://github.com/GrooveLabs/monorepo/pull/184
              container.current.style.overflowX = 'scroll';
            } else {
              container.current.style.overflowX = 'auto';
            }
            window.parent.postMessage(
              {
                type: LIGHTNING_CONTAINER_SIZE_CHANGE,
                payload: {
                  iframeId: payload.iframeId,
                  clientWidth: target.clientWidth,
                  clientHeight: target.clientHeight,
                  scrollWidth: target.scrollWidth,
                  scrollHeight: target.scrollHeight,
                },
              },
              ev.origin
            );
          }
        });
        observer.observe(container.current);

        await lightningOut.waitForAccessToken();
        await lightningOut.render(
          container.current,
          payload.lightningComponents,
          payload.recordId
        );

        window.parent.postMessage(
          { type: LIGHTNING_RENDER_COMPLETED },
          ev.origin
        );
      }
    };

    window.addEventListener('message', messageHandler, true);
    return (): void => {
      window.removeEventListener('message', messageHandler, true);
    };
  }, []);

  return <div ref={container} />;
};

export default ModuleRoot;
